/**
 * Application configuration constants
 */

// Backend API configuration
export const API_CONFIG = {
  // Backend base URL - use localhost in development, production URL otherwise
  BASE_URL: process.env.NODE_ENV === 'development'
    ? process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8080/api'  // Local development
    : process.env.NEXT_PUBLIC_API_BASE_URL || 'https://omnia-xrzvkq.fly.dev/api',  // Production

  // Default tenant ID
  DEFAULT_TENANT_ID: '1',

  // Authentication endpoints
  ENDPOINTS: {
    LOGIN: '/auth/login',
    PASSWORD_RESET_REQUEST: '/auth/password-reset/request',
    PASSWORD_RESET_CONFIRM: '/auth/password-reset/confirm',
  },

  // Request timeout in milliseconds
  TIMEOUT: 10000,

  // Storage keys for localStorage
  STORAGE_KEYS: {
    TOKEN: 'auth_token',
  },
} as const;

// Vimeo video configuration for the free class page
export const VIMEO_CONFIG = {
  // Default video ID - can be overridden by environment variable
  DEFAULT_VIDEO_ID: '1116500967', // Replace with actual Vimeo video ID
  
  // Get video ID from environment variable or use default
  getVideoId: () => process.env.NEXT_PUBLIC_VIMEO_VIDEO_ID || VIMEO_CONFIG.DEFAULT_VIDEO_ID,

  // Background video ID - can be overridden by environment variable
  DEFAULT_BACKGROUND_VIDEO_ID: '1127452813', // Replace with actual Vimeo video ID

  // Get background video ID from environment variable or use default
  getBackgroundVideoId: () => process.env.NEXT_PUBLIC_VIMEO_BACKGROUND_VIDEO_ID || VIMEO_CONFIG.DEFAULT_BACKGROUND_VIDEO_ID,
  
  // Vimeo embed URL template
  getEmbedUrl: (videoId?: string) => {
    const id = videoId || VIMEO_CONFIG.getVideoId();
    return `https://player.vimeo.com/video/${id}`;
  },
  
  // Vimeo embed parameters for optimal playback
  EMBED_PARAMS: {
    autoplay: 1,
    muted: 1, // Required for autoplay in most browsers
    loop: 0,
    color: 'ff2222',
    badge: 0,
    title: 0,
    byline: 0,
    portrait: 0,
    controls: 1,
    responsive: 1,
    vimeo_logo: 0,
  } as const,
  
  // Build complete embed URL with parameters
  getFullEmbedUrl: (videoId?: string) => {
    const baseUrl = VIMEO_CONFIG.getEmbedUrl(videoId);
    const params = new URLSearchParams();
    
    Object.entries(VIMEO_CONFIG.EMBED_PARAMS).forEach(([key, value]) => {
      params.append(key, value.toString());
    });
    
    return `${baseUrl}?${params.toString()}`;
  },
} as const;

// Video player configuration
export const VIDEO_PLAYER_CONFIG = {
  // Responsive aspect ratio (16:9)
  ASPECT_RATIO: '56.25%', // 9/16 * 100

  // Loading states
  LOADING_TIMEOUT: 10000, // 10 seconds

  // Error retry configuration
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000, // 2 seconds
} as const;

// YouTube live stream configuration for the launch masterclass room
export const YOUTUBE_CONFIG = {
  // Feature flag: enable live chat embed (set to true when going live)
  LIVE_CHAT_ENABLED: false,

  // Default video/live stream ID - can be overridden by environment variable
  DEFAULT_VIDEO_ID: 'aotqzQ6ozcU', // Replace with actual YouTube video/live stream ID

  // Get video ID from environment variable or use default
  getVideoId: () => process.env.NEXT_PUBLIC_YOUTUBE_VIDEO_ID || YOUTUBE_CONFIG.DEFAULT_VIDEO_ID,

  // YouTube embed URL template
  getEmbedUrl: (videoId?: string) => {
    const id = videoId || YOUTUBE_CONFIG.getVideoId();
    return `https://www.youtube.com/embed/${id}`;
  },

  // YouTube embed parameters for optimal playback
  EMBED_PARAMS: {
    autoplay: 0,
    mute: 0, // Required for autoplay in most browsers
    controls: 1,
    modestbranding: 1,
    rel: 0,
    enablejsapi: 1,
  } as const,

  // Build complete embed URL with parameters
  getFullEmbedUrl: (videoId?: string) => {
    const baseUrl = YOUTUBE_CONFIG.getEmbedUrl(videoId);
    const params = new URLSearchParams();

    Object.entries(YOUTUBE_CONFIG.EMBED_PARAMS).forEach(([key, value]) => {
      params.append(key, value.toString());
    });

    return `${baseUrl}?${params.toString()}`;
  },

  // Build live chat embed URL
  getLiveChatUrl: (videoId?: string, embedDomain?: string) => {
    const id = videoId || YOUTUBE_CONFIG.getVideoId();
    const domain = embedDomain || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
    return `https://www.youtube.com/live_chat?v=${id}&embed_domain=${domain}`;
  },
} as const;
